import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import type { ExternalIdentifier, Pro } from 'types/writer'

import { editPro } from '@disco/state-management/actions/rights-data-actions'
import { useGetRightsHolderByIdQuery } from '@disco/state-management/api/rights-holders'
import { updateSplit } from '@disco/state-management/reducers/rightsManagement'
import { getPros } from '@disco/state-management/selectors/contacts'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { BaseDialog } from '../../components/base-dialog'
import { useRightsNewModelEnabled, useUpdateRightsEntity } from '../../hooks'
import type { RightsDataModalLocationState } from '../../types'
import { findDuplicateName } from '../../utils/validation'

/**
 * EditProModal - Modal for editing an existing PRO (Performance Rights Organization)
 *
 * This modal allows users to edit a PRO's name and ISNI.
 * The PRO ID is extracted from the route params.
 *
 * When edited from a split row (via splitId and splitsKey in location state),
 * the split is automatically updated with the edited PRO to ensure it
 * remains selected in the dropdown.
 */
export function EditProModal() {
  const dispatch = useDispatch()
  const location = useLocation()
  const { proId } = useParams<{ proId: string }>()
  const { splitId, splitsKey } =
    (location.state as RightsDataModalLocationState) || {}

  const rightsNewModelEnabled = useRightsNewModelEnabled()

  // New API: fetch by ID from route param
  const { data: rightsHolderData } = useGetRightsHolderByIdQuery(
    proId ? parseInt(proId, 10) : 0,
    { skip: !rightsNewModelEnabled || !proId },
  )

  // Old API: lookup from Redux
  const pros = useSelector(getPros)
  const proFromRedux = pros.find((p: Pro) => p.id.toString() === proId)

  // Use new API data or fall back to old Redux data
  const pro: Pro | undefined = useMemo(() => {
    if (rightsNewModelEnabled) {
      return rightsHolderData
        ? {
            id: rightsHolderData.id,
            name: rightsHolderData.title,
            external_identifiers: rightsHolderData.external_identifiers,
          }
        : undefined
    }
    return proFromRedux
  }, [rightsNewModelEnabled, rightsHolderData, proFromRedux])

  const [inputValue, setInputValue] = useState(pro?.name || '')

  // Sync inputValue when pro changes (e.g., async load)
  useEffect(() => {
    if (pro?.name) {
      setInputValue(pro.name)
    }
  }, [pro?.name])

  const duplicateProName = useMemo(
    () => (pro ? findDuplicateName(inputValue, pros, pro.id) : null),
    [inputValue, pros, pro],
  )

  const warningMessage = duplicateProName
    ? `${duplicateProName} already exists. Please double-check if this is a duplicate before saving.`
    : null

  const handleNameChange = useCallback((value: string) => {
    setInputValue(value)
  }, [])

  const updateEntity = useUpdateRightsEntity({
    entityType: 'PRO',
    updateThunk: editPro,
  })

  const handleSave = useCallback(
    async (
      name: string,
      externalIdentifiers: ExternalIdentifier[] | undefined,
    ) => {
      if (!pro) return

      await updateEntity(
        pro.id,
        name,
        externalIdentifiers,
        pro.external_identifiers,
      )

      // Auto-select the edited PRO in the split that triggered the edit
      if (splitId && splitsKey) {
        dispatch(
          updateSplit({
            trackId: splitsKey,
            splitId,
            changes: { proId: pro.id },
          }),
        )
      }
    },
    [pro, updateEntity, dispatch, splitId, splitsKey],
  )

  // Don't render if PRO not found
  if (!pro) {
    return null
  }

  return (
    <BaseDialog
      heading="Edit PRO"
      nameFieldLabel="PRO name"
      initialName={pro.name}
      initialIdentifiers={pro.external_identifiers}
      helperText="Editing this PRO will update it everywhere it's used."
      onSave={handleSave}
      isEditMode={true}
      warningMessage={warningMessage}
      onNameChange={handleNameChange}
      showISNI
      open
    />
  )
}

export default EditProModal
